import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Pressable, StyleSheet, View } from 'react-native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from '@xbto/design-system';
import { Col, Surface, TextBody } from '../../atoms';
export function Tabs({ tabs, tabsRightAddOn, panels, initialIndex = 0, forceInitialTabIndex = 0, onTabChange, tabStyle = 'default', fullWidth, tabsFullWidth, }) {
    const [selectedIndex, setSelectedIndex] = useState(initialIndex);
    useEffect(() => {
        if (forceInitialTabIndex !== 0) {
            setSelectedIndex(initialIndex);
        }
    }, [forceInitialTabIndex, initialIndex]);
    const selectedItem = useMemo(() => {
        return tabs.at(selectedIndex);
    }, [tabs, selectedIndex]);
    const handleChange = useCallback((key) => {
        setSelectedIndex(key);
        return onTabChange === null || onTabChange === void 0 ? void 0 : onTabChange(tabs[key].key);
    }, [onTabChange, tabs]);
    const Root = fullWidth ? Col.LRC : Col.L;
    const TabList = tabStyle === 'switcher' ? TabListSwitcher : TabListDefault;
    return (_jsxs(Root, { children: [_jsx(TabList, { tabs: tabs, selectedItem: selectedItem, handleChange: handleChange, tabsFullWidth: tabsFullWidth, tabsRightAddOn: tabsRightAddOn }), _jsx(Col.LRC, { style: $.panelContainer, children: panels.at(selectedIndex) })] }));
}
const TabListSwitcher = ({ tabs, tabsRightAddOn, selectedItem, handleChange, tabsFullWidth, }) => {
    return (_jsxs(View, { style: $.tabsWrapper, children: [_jsx(View, { style: [$.tabsSwitcherContainer, tabsFullWidth && $.tabFullWidth], children: _jsx(View, { style: [
                        $.tabsSwitcherBackgroundContainer,
                        tabsFullWidth && $.tabFullWidth,
                    ], children: tabs.map((t, index) => {
                        return (_jsx(TabItemSwitcher, { active: (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.key) === t.key, index: index, fluid: tabsFullWidth, title: t.title, onPress: handleChange }, index));
                    }) }) }), _jsx(_Fragment, { children: tabsRightAddOn ? _jsx(Col.C, { children: tabsRightAddOn }) : null })] }));
};
const TabListDefault = ({ tabs, tabsRightAddOn, selectedItem, handleChange, tabsFullWidth, }) => {
    return (_jsxs(View, { style: [$.tabsWrapper, $.tabsDefaultWrapper], children: [_jsx(View, { style: [$.tabsDefaultContainer, tabsFullWidth && $.tabFullWidth], children: tabs.map((t, index) => {
                    return (_jsx(TabItemDefault, { active: (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.key) === t.key, index: index, title: t.title, onPress: handleChange }, index));
                }) }), _jsx(_Fragment, { children: tabsRightAddOn ? _jsx(Col.C, { children: tabsRightAddOn }) : null })] }));
};
export const TabItemDefault = ({ active, index, onPress, small = false, title, }) => {
    // Handlers
    const handlePress = useCallback(() => {
        onPress === null || onPress === void 0 ? void 0 : onPress(index);
    }, [index, onPress]);
    // Render
    return (_jsx(Pressable, { disabled: !onPress, onPress: handlePress, style: [$.tabDefault, active && $.tabDefaultActive], children: _jsx(TextBody, { small: small, color: active ? 'primary' : 'tertiary', em: true, lhFull: true, children: title }) }, index));
};
export const TabItemSwitcher = ({ active, index, fluid = false, small = false, title, height, onPress, }) => {
    // Handlers
    const handlePress = useCallback(() => {
        onPress === null || onPress === void 0 ? void 0 : onPress(index);
    }, [index, onPress]);
    // Render
    return (_jsx(Surface, { disabled: !onPress, onPress: handlePress, variant: active ? 'primary' : 'tertiary', rounded: "sm", style: [$.tabSwitcher, fluid && $.tabFullWidth, { height }], children: _jsx(TextBody, { small: small, em: true, color: active ? 'inverse' : 'primary', lhFull: true, children: title }) }, index));
};
const $ = StyleSheet.create({
    tabsWrapper: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
    },
    tabsDefaultWrapper: {
        paddingVertical: theme.spacing['6'],
    },
    tabsSwitcherContainer: {
        borderRadius: theme.radius.sm,
        flexDirection: 'row',
        padding: theme.spacing['0.5'],
        backgroundColor: theme.color.background.tertiary,
    },
    tabsSwitcherBackgroundContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    tabSwitcher: {
        paddingVertical: theme.spacing['2'],
        paddingHorizontal: theme.spacing['4'],
        borderRadius: theme.radius.sm,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    tabsDefaultContainer: {
        flexDirection: 'row',
    },
    tabDefault: {
        height: 25,
        paddingHorizontal: theme.spacing['2'],
        borderBottomColor: theme.color.border.secondary,
        borderBottomWidth: 1,
        alignItems: 'center',
    },
    tabDefaultActive: {
        borderBottomColor: theme.color.border.tertiary,
        borderBottomWidth: 2,
    },
    tabFullWidth: { flexGrow: 1 },
    panelContainer: {
        width: '100%',
    },
});
